/**
 * React Query Hooks for Portfolio Management
 *
 * Provides hooks for fetching portfolio data with automatic
 * caching and retry logic.
 */

import { useQuery } from '@tanstack/react-query';
import { portfolioAPI } from '../../services/tradingBotAPI';
import { queryKeys, staleTimeConfig } from '../../config/queryClient';
import type { PortfolioSnapshot } from '../../types';

/**
 * Fetch current portfolio snapshot
 *
 * @returns Query with current portfolio data
 */
export function usePortfolio() {
  return useQuery({
    queryKey: queryKeys.portfolio.detail(),
    queryFn: async () => {
      const response = await portfolioAPI.getPortfolio();
      return response.data.data as PortfolioSnapshot;
    },
    staleTime: staleTimeConfig.frequent, // 2 minutes
  });
}

/**
 * Fetch portfolio history
 *
 * @param days - Number of days of history to fetch (default: 30)
 * @returns Query with portfolio history
 */
export function usePortfolioHistory(days: number = 30) {
  return useQuery({
    queryKey: queryKeys.portfolio.history(days),
    queryFn: async () => {
      const response = await portfolioAPI.getHistory(days);
      return response.data.data;
    },
    staleTime: staleTimeConfig.slow, // 15 minutes (historical data changes slowly)
  });
}

/**
 * Fetch portfolio allocation
 *
 * @returns Query with portfolio asset allocation
 */
export function usePortfolioAllocation() {
  return useQuery({
    queryKey: queryKeys.portfolio.allocation(),
    queryFn: async () => {
      const response = await portfolioAPI.getAllocation();
      return response.data.data;
    },
    staleTime: staleTimeConfig.frequent, // 2 minutes
  });
}
